import FullscreenForm from "@/components/layout/fullscreen-form"
import { Button } from "@/components/ui/button"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { paymentLinkSchema } from "./payment-link.schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form } from "@/components/ui/form"
import PaymentLinkForm from "./payment-link.form"
import { PaymentLinkType, usePaymentLinksActions } from "@/api/payment-links"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Token } from "@/config/tokens"
import PayPreview from "./pay-preview"
import { DesktopOnly } from "@/components/ux/desktop-only"
import { getNetworksForToken } from "@/config"

const transform = ({
  amount,
  minAmount,
  maxAmount,
  presetAmounts,
  ...data
}: z.input<typeof paymentLinkSchema>) => ({
  ...data,
  amount: amount !== undefined && amount !== "" ? Number(amount) : undefined,
  minAmount: minAmount !== undefined ? Number(minAmount) : undefined,
  maxAmount: maxAmount !== undefined ? Number(maxAmount) : undefined,
  presetAmounts: presetAmounts
    ?.filter((amount) => amount && amount !== "")
    .map(Number),
})

export default function PaymentLinkCreateScreen() {
  const { create } = usePaymentLinksActions()
  const [isCreating, setIsCreating] = useState(false)
  const navigate = useNavigate()
  const form = useForm<
    z.input<typeof paymentLinkSchema>,
    undefined,
    z.output<typeof paymentLinkSchema>
  >({
    resolver: zodResolver(paymentLinkSchema),
    defaultValues: {
      type: PaymentLinkType.FIXED_AMOUNT,
      title: "",
      description: "",
      networks: getNetworksForToken(Token.USDC),
      token: Token.USDC,
      active: true,
    },
  })

  const onSubmit = async (data: z.infer<typeof paymentLinkSchema>) => {
    setIsCreating(true)
    const paymentLink = await create(data)
    setIsCreating(false)
    navigate(`/payment-links/${paymentLink.id}`)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FullscreenForm
          backTo="/payment-links"
          buttons={[
            <Button
              key="create"
              variant="default"
              disabled={isCreating}
              type="submit"
            >
              Create Link
            </Button>,
          ]}
        >
          <div className="flex flex-grow flex-col md:flex-row flex-shrink min-h-0">
            <div className="flex-grow p-12">
              <div className="text-lg font-semibold leading-none tracking-tight pb-4">
                Create Payment Link
              </div>
              <PaymentLinkForm />
            </div>
            <DesktopOnly>
              <div className="md:w-6/12 lg:w-8/12 flex-grow">
                <PayPreview paymentLink={transform(form.watch())} />
              </div>
            </DesktopOnly>
          </div>
        </FullscreenForm>
      </form>
    </Form>
  )
}
